import { useEffect, useMemo, useRef, useState } from 'react'
import type { Clinic } from '../types'
import { useBrowserBackSentinel } from '../utils/useBrowserBackSentinel'
import {
  subscribeRecoveryCases,
  type RecoveryCase,
} from '../services/recoveryCases'
import {
  recoveryBucket,
  recoveryStatusChip,
  RECOVERY_BUCKET_LABEL,
  type RecoveryBucket,
} from '../utils/recoveryStepper'
import {
  DEFAULT_TZ,
  windowStartUtc,
  unionWindowStartUtc,
  type WindowOption,
} from '../utils/recentCheckWindow'
import { formatDateShortUs, formatIsoDateShort } from '../utils/formatters'
import RecoveryStepper from './RecoveryStepper'
import RecoveryLoopSlideout from './RecoveryLoopSlideout'

interface RecoveryLoopListProps {
  orgId: string
  /** The caller's clinic — required to scope non-admin reads. */
  clinicId?: string | null
  isAdmin: boolean
  isStaff: boolean
  /** False for a view-mode staff impersonation: the drawer's card rows render
   *  read-only and Save / Re-check stay disabled, the same treatment the check
   *  panels give a read-only session. Default true (a tenant's own user). */
  canWrite?: boolean
  clinics: Clinic[]
  /** Deep-link from a check slideout: auto-open this check's case on the
   *  Conversation tab once the list has loaded. Consumed via onFocusConsumed. */
  focusCheckId?: string | null
  onFocusConsumed?: () => void
  /** Open an eligibility check's slideout for a case — `checkId` is the
   *  drawer's link target (#279): the fired re-run when one exists, else the
   *  case's originating check. */
  onOpenCheck?: (rc: RecoveryCase, checkId: string) => void
}

/** ISO YYYY-MM-DD → MM/DD/YYYY for display. */
function fmtDob(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  return m ? `${m[2]}/${m[3]}/${m[1]}` : iso
}

/** Compact relative timestamp for the row's last activity. */
function fmtAgo(iso: string): string {
  const t = new Date(iso).getTime()
  if (isNaN(t)) return ''
  const mins = Math.floor((Date.now() - t) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return days < 7 ? `${days}d ago` : formatDateShortUs(new Date(t))
}

const BUCKET_ORDER: RecoveryBucket[] = [
  'waiting', 'in_progress', 'staff_review', 'recovered', 'closed_by_staff', 'opted_out',
]

/**
 * The four numbers worth a headline tile, in the prototype's order. Deliberately
 * NOT every bucket: two are the work ("who needs me, who's the system chasing")
 * and two are the outcome — and those two stay side by side precisely because
 * they must never be read as one number (Recovered ≠ Closed by staff).
 */
const KPI_TILES: Array<{ bucket: RecoveryBucket; label: string; tone: string }> = [
  { bucket: 'waiting', label: 'Waiting on patient', tone: 'amber' },
  { bucket: 'staff_review', label: 'Staff review', tone: 'red' },
  { bucket: 'recovered', label: 'Recovered by loop', tone: 'green' },
  { bucket: 'closed_by_staff', label: 'Closed by staff', tone: 'gray' },
]

export default function RecoveryLoopList({
  orgId, clinicId, isAdmin, isStaff, canWrite = true, clinics, focusCheckId, onFocusConsumed,
  onOpenCheck,
}: RecoveryLoopListProps) {
  const [cases, setCases] = useState<RecoveryCase[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [bucket, setBucket] = useState<RecoveryBucket | 'all'>('all')
  const [clinicFilter, setClinicFilter] = useState<string>('all')
  const [windowOption, setWindowOption] = useState<WindowOption>('all')
  const [selected, setSelected] = useState<RecoveryCase | null>(null)
  const [slideoutTab, setSlideoutTab] = useState<'extraction' | 'conversation'>('extraction')
  const [sortBy, setSortBy] = useState<'activity' | 'appointment' | 'name'>('activity')

  // Non-admin: always scoped to their own clinic (rules require the constraint).
  // Admin: org-wide, or narrowed by the clinic dropdown.
  const queryClinicId = isAdmin
    ? (clinicFilter === 'all' ? undefined : clinicFilter)
    : (clinicId ?? undefined)

  /**
   * Server-side lower bound on the case's `checkedAt` — same semantics as
   * Recent Checks: 'all' → null; specific clinic context → that clinic's TZ
   * midnight; admin "All Clinics" → earliest across clinic TZs (safe over-fetch).
   */
  const windowStart = useMemo<Date | null>(() => {
    if (windowOption === 'all') return null
    const now = new Date()
    if (isAdmin && clinicFilter === 'all') return unionWindowStartUtc(windowOption, clinics, now)
    const targetId = isAdmin ? clinicFilter : clinicId
    const tz = (targetId && clinics.find((c) => c.id === targetId)?.timezone) || DEFAULT_TZ
    return windowStartUtc(windowOption, tz, now)
  }, [windowOption, isAdmin, clinicFilter, clinicId, clinics])

  // Live subscription — the checks cohort + per-case conversation listeners.
  // Re-created whenever the query scope changes; torn down on unmount. Rows
  // move on their own (card received, extraction, mark handled) — no reloads.
  // On a scope change the previous rows stay up until the new subscription's
  // first emission lands (stale-while-loading; the swap is sub-second).
  useEffect(() => subscribeRecoveryCases(
    orgId,
    queryClinicId,
    windowStart,
    (cs) => {
      setCases(cs)
      setError(null)
      setLoading(false)
    },
    (err) => {
      setError(err.message || 'Failed to load recovery cases.')
      setLoading(false)
    },
  ), [orgId, queryClinicId, windowStart])

  // Deep-link arrival from a check slideout: once cases are in, open the
  // matching case straight onto its conversation. Consumed whether or not a
  // match exists (a stale/filtered-out id just lands on the list).
  useEffect(() => {
    if (!focusCheckId || loading) return
    const match = cases.find((c) => c.checkId === focusCheckId)
    // Deferred so no setState runs synchronously in the effect body
    // (react-hooks/set-state-in-effect). Re-fires are idempotent — the parent
    // clears focusCheckId via onFocusConsumed, which ends the loop.
    queueMicrotask(() => {
      if (match) {
        setSlideoutTab('conversation')
        setSelected(match)
      }
      onFocusConsumed?.()
    })
  }, [focusCheckId, loading, cases, onFocusConsumed])

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: cases.length }
    for (const rc of cases) {
      const b = recoveryBucket(rc.displayState, rc.handled)
      c[b] = (c[b] ?? 0) + 1
    }
    return c
  }, [cases])

  const visible = useMemo(
    () => bucket === 'all'
      ? cases
      : cases.filter((c) => recoveryBucket(c.displayState, c.handled) === bucket),
    [cases, bucket],
  )

  // Client-side sort of the visible rows. 'activity' matches the subscription's
  // own ordering; 'appointment' = soonest first (no date sorts last); 'name' =
  // last name then first.
  const sorted = useMemo(() => {
    const arr = [...visible]
    switch (sortBy) {
      case 'name':
        return arr.sort((a, b) =>
          (a.lastName || a.firstName).localeCompare(b.lastName || b.firstName) ||
          a.firstName.localeCompare(b.firstName))
      case 'appointment':
        return arr.sort((a, b) =>
          (a.appointmentDate ?? '9999-99-99').localeCompare(b.appointmentDate ?? '9999-99-99'))
      default:
        return arr.sort((a, b) => (b.lastActivity ?? '').localeCompare(a.lastActivity ?? ''))
    }
  }, [visible, sortBy])

  // `selected` is a click-time snapshot; the drawer should track live updates
  // (card received, extraction) — render the fresh copy by id, falling back to
  // the snapshot if the case just left the query window.
  const selectedLive = selected
    ? cases.find((c) => c.checkId === selected.checkId) ?? selected
    : null

  // Browser Back closes the drawer (#349 A11). The sentinel lives HERE, in the
  // component that stays mounted across the close: a history slot is reclaimed
  // only when the open flag flips false while the hook is mounted, and the
  // drawer unmounts on close — registered inside it, every in-app close left
  // one entry behind and the next Back press did nothing (#231). Same shape as
  // Dashboard's detail views: the parent owns the sentinel, the panel owns the
  // decision (a dirty draft refuses the first press, and the hook re-arms).
  //
  // Known gap, and a NEW one (the list pushed no entry before #349): "View
  // check →" navigates away and unmounts this list with the drawer still open,
  // which strands that one slot. Reclaiming it on that path is not safe — the
  // queued `history.back()` would land AFTER the check slideout pushed its own
  // entry and close it — so the cost is one dead press after returning from
  // the check.
  const drawerBack = useRef<(() => void | 'refused') | null>(null)
  useBrowserBackSentinel(!!selectedLive, () => {
    if (drawerBack.current) return drawerBack.current()
    setSelected(null)
  })

  return (
    <main className="dashboard-main">
      <div className="recovery-loop">
        <div className="recovery-loop__head">
          <div>
            <h2 className="recovery-loop__title">Recovery Loop</h2>
            <p className="recovery-loop__subtitle">
              Automated patient outreach for eligibility failures · {cases.length} case{cases.length === 1 ? '' : 's'}
            </p>
          </div>
          <div className="recovery-loop__head-controls">
            <div className="recent-checks__segmented" role="group" aria-label="Time window">
              {([
                ['today', 'Today'],
                ['last7days', '7 days'],
                ['last30days', '30 days'],
                ['all', 'All time'],
              ] as const).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={`recent-checks__segment${windowOption === value ? ' recent-checks__segment--active' : ''}`}
                  aria-pressed={windowOption === value}
                  onClick={() => setWindowOption(value)}
                >
                  {label}
                </button>
              ))}
            </div>
            {isAdmin && clinics.length > 1 && (
              <select
                className="recent-checks__clinic-filter"
                value={clinicFilter}
                onChange={(e) => setClinicFilter(e.target.value)}
              >
                <option value="all">All Clinics</option>
                {clinics.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
          </div>
        </div>

        {/* Headline counts — the four numbers a receptionist scans first.
            Each tile is also a filter, so the number and the queue behind it
            are always the same set. */}
        <div className="recovery-loop__kpis">
          {KPI_TILES.map((tile) => (
            <button
              key={tile.bucket}
              type="button"
              className={`recovery-kpi recovery-kpi--${tile.tone}${bucket === tile.bucket ? ' recovery-kpi--active' : ''}`}
              aria-pressed={bucket === tile.bucket}
              onClick={() => setBucket(bucket === tile.bucket ? 'all' : tile.bucket)}
            >
              <span className="recovery-kpi__n">{counts[tile.bucket] ?? 0}</span>
              <span className="recovery-kpi__l">{tile.label}</span>
            </button>
          ))}
        </div>

        {/* Bucket chips (left rail in the mock; a chip row here) */}
        <div className="recovery-loop__toolbar">
          <div className="recovery-loop__buckets" role="group" aria-label="Filter by status">
            <button
              type="button"
              className={`recovery-loop__bucket${bucket === 'all' ? ' recovery-loop__bucket--active' : ''}`}
              aria-pressed={bucket === 'all'}
              onClick={() => setBucket('all')}
            >
              All <span className="recovery-loop__bucket-count">{counts.all ?? 0}</span>
            </button>
            {BUCKET_ORDER.map((b) => (
              <button
                key={b}
                type="button"
                className={`recovery-loop__bucket${bucket === b ? ' recovery-loop__bucket--active' : ''}`}
                aria-pressed={bucket === b}
                onClick={() => setBucket(b)}
              >
                {RECOVERY_BUCKET_LABEL[b]} <span className="recovery-loop__bucket-count">{counts[b] ?? 0}</span>
              </button>
            ))}
          </div>
          <label className="recovery-loop__sort">
            Sort
            <select
              className="recent-checks__clinic-filter"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'activity' | 'appointment' | 'name')}
            >
              <option value="activity">Last activity</option>
              <option value="appointment">Appointment date</option>
              <option value="name">Name</option>
            </select>
          </label>
        </div>

        {error && <div className="error-banner">{error}</div>}

        {loading ? (
          <div className="recovery-loop__empty">Loading recovery cases…</div>
        ) : visible.length === 0 ? (
          <div className="recovery-loop__empty">
            {cases.length > 0
              ? 'No cases in this status.'
              : windowOption !== 'all'
                ? 'No recovery cases in this window. Select “All time” to see history.'
                : 'No recovery cases yet. Failed checks that reach a patient for a card photo will appear here.'}
          </div>
        ) : (
          <ul className="recovery-loop__list">
            {sorted.map((c) => {
              const chip = recoveryStatusChip(c.displayState, c.handled)
              return (
                <li key={c.checkId}>
                  <button
                    type="button"
                    className={`recovery-case${selected?.checkId === c.checkId ? ' recovery-case--active' : ''}`}
                    onClick={() => {
                      // Prototype rule: no extracted data yet → land on the conversation.
                      setSlideoutTab(c.extraction ? 'extraction' : 'conversation')
                      setSelected(c)
                    }}
                  >
                    <div className="recovery-case__head">
                      <span className="recovery-case__name">
                        {c.lastName || c.firstName ? `${c.firstName} ${c.lastName}`.trim() : '—'}
                      </span>
                      {/* Separators are their own muted elements with even
                          margins, not literal "·" inside the string — that's
                          what made the header read as unevenly spaced (#216). */}
                      <span className="recovery-case__meta">
                        {[
                          c.reasonLabel,
                          c.dob ? `DOB ${fmtDob(c.dob)}` : null,
                          c.appointmentDate ? `appt ${formatIsoDateShort(c.appointmentDate)}` : null,
                        ].filter(Boolean).map((part, i) => (
                          <span key={i}>
                            <span className="recovery-case__dot" aria-hidden="true">·</span>
                            {part}
                          </span>
                        ))}
                      </span>
                      <span className="recovery-case__right">
                        <span className={`rl-status-chip rl-status-chip--${chip.hue}`}>{chip.label}</span>
                        {c.lastActivity && <span className="recovery-case__ago">{fmtAgo(c.lastActivity)}</span>}
                      </span>
                    </div>
                    <RecoveryStepper state={c.displayState} />
                  </button>
                </li>
              )
            })}
          </ul>
        )}

        <div className="recovery-loop__phi">
          <span aria-hidden="true">🔒</span> PHI protection active. SMS contains first name only —
          no insurance details or diagnosis. Card images stored server-side, org-scoped, no public URLs.
        </div>
      </div>

      {selectedLive && (
        <RecoveryLoopSlideout
          key={`${selectedLive.checkId}:${slideoutTab}`}
          case={selectedLive}
          orgId={orgId}
          isStaff={isStaff}
          canWrite={canWrite}
          clinic={clinics.find((c) => c.id === selectedLive.clinicId) ?? null}
          onClose={() => setSelected(null)}
          onHandled={() => setSelected(null)}
          initialTab={slideoutTab}
          onOpenCheck={onOpenCheck ? (checkId: string) => onOpenCheck(selectedLive, checkId) : undefined}
          backHandlerRef={drawerBack}
        />
      )}
    </main>
  )
}
